import type { ReactNode } from "react";
import { Button, Table, Tooltip } from "antd";
import { DeleteOutlined, LogoutOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import type { ColumnsType, TableProps } from "antd/es/table";
import type { TeamMember } from "../types";
import { EMAIL_VERIFICATION_FLAG, isHostFeatureEnabled } from "../../host/hostFeatureFlags";
import {
  EmailVerificationBadge,
  shouldShowVerificationBadge,
} from "./EmailVerificationBadge";
import { MemberAvatar } from "./MemberAvatar";
import { MemberRoleSelection } from "./MemberRoleSelection";
import { MemberLicenseSelection } from "./MemberLicenseSelection";
import { hasPersonalPlan, isRoleActionableByViewer } from "../memberPlanRules";
import { memberDisplayName } from "../memberDisplay";
import styles from "./MembersTable.module.css";

dayjs.extend(relativeTime);

export interface MembersTableProps {
  members: TeamMember[];
  loading: boolean;
  currentUserId: string | number | undefined;
  /** Viewer's role on the team — drives which rows are editable. */
  currentUserAccountRole: string | undefined;
  currentUserIsTeamOwnerOrAdmin: boolean;
  isSelfCreateTeam: boolean;
  showDetailedColumns: boolean;
  showActionColumn: boolean;
  scimEnabled: boolean;
  /**
   * AHA-43040: Unleash override (`AHA-42627-scim-manual-management`) that
   * lets SCIM-provisioned teams (ATB et al) still manage members manually
   * — combine with `scimEnabled` at every role/licence/remove/leave gate.
   */
  canUseScimManualManagement: boolean;
  /** The team's current plan name (account.plan) — shown in the License
   *  column for licensed members and as the grant option. */
  teamPlan: string | null | undefined;
  /** Whether the team is on a paid plan — there's nothing to grant on a
   *  free team, so the License select stays read-only then. */
  isPaidTeamPlan: boolean;
  /** AHA-43204: seats available right now (quota - used > 0). Threaded into
   *  MemberLicenseSelection so the Grant menu item disables with a tooltip
   *  when the team is at cap, instead of letting the user hit a 400. */
  canGrantMore: boolean;
  /** Member id whose role-change is in flight — drives the per-row
   *  spinner in MemberRoleSelection. */
  updatingMemberId: string | null;
  /** Member id whose license-change is in flight — per-row spinner in
   *  MemberLicenseSelection. */
  updatingLicenseMemberId: string | null;
  onUpdateMemberRole: (member: TeamMember, role: string) => void;
  onUpdateMemberLicense: (member: TeamMember, licensed: boolean) => void;
  onRemove: (member: TeamMember) => void;
  onLeave: (member: TeamMember) => void;
  /** AHA-41755 bulk-action: controlled selection state. Undefined disables
   *  selection entirely (no checkbox column). */
  selectedMemberIds?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
  /** Toolbar content rendered inside the table card, above the column
   *  header and divided from it by a border (e.g. the Export button). */
  header?: ReactNode;
}

export function MembersTable({
  members,
  loading,
  currentUserId,
  currentUserAccountRole,
  currentUserIsTeamOwnerOrAdmin,
  isSelfCreateTeam,
  showDetailedColumns,
  showActionColumn,
  scimEnabled,
  canUseScimManualManagement,
  teamPlan,
  isPaidTeamPlan,
  canGrantMore,
  updatingMemberId,
  updatingLicenseMemberId,
  onUpdateMemberRole,
  onUpdateMemberLicense,
  onRemove,
  onLeave,
  selectedMemberIds,
  onSelectionChange,
  header,
}: MembersTableProps) {
  const { t } = useTranslation();
  const fallback = t("team.role.member");
  // AHA-43040: every SCIM gate honours the manual-management override.
  // Local const so the four call sites below stay terse.
  const canManageManually = !scimEnabled || canUseScimManualManagement;
  // AHA-39565: show the unverified-email badge only when the host flag is on.
  // Plain lookup (host xprops are static for the session) — no reactivity.
  const showVerificationBadges = isHostFeatureEnabled(EMAIL_VERIFICATION_FLAG);

  const canRemove = (m: TeamMember): boolean =>
    String(m.id) !== String(currentUserId) &&
    currentUserIsTeamOwnerOrAdmin &&
    m.accountRole !== "owner" &&
    canManageManually;

  const canLeave = (m: TeamMember): boolean =>
    String(m.id) === String(currentUserId) && isSelfCreateTeam && canManageManually;

  const columns: ColumnsType<TeamMember> = [
    {
      title: t("team.column.name"),
      key: "name",
      sorter: (a, b) =>
        memberDisplayName(a, fallback).localeCompare(memberDisplayName(b, fallback), undefined, {
          sensitivity: "base",
        }),
      render: (_value: unknown, record: TeamMember) => {
        const name = memberDisplayName(record, fallback);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <MemberAvatar id={record.id} name={name} image={record.avatar} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14, lineHeight: "20px" }}>{name}</div>
              {/* AHA-43202: render as plain text — clicking the previous
                  `mailto:` anchor inside the zoid iframe opened a new tab
                  outside the host or surfaced a navigation/content error
                  (Edge repro spawned multiple tabs). There's no member-
                  detail page to navigate to either, so a non-interactive
                  label is the right shape until we have one. testid kept
                  so the existing e2e selectors (tests/objects/pages/
                  TeamPage.ts:99) still resolve. */}
              <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                <span
                  data-testid="my-team-member-email-link"
                  style={{ fontSize: 12, color: "#717171", lineHeight: "18px" }}
                >
                  {record.email}
                </span>
                {/* AHA-39565: unverified-email badge. Gate here on
                    shouldShowVerificationBadge so verified / SCIM / no-email rows
                    never mount the component (and its resend hook). The signed-in
                    user's own row gets the interactive Verify now / Resend
                    treatment; every other row gets the read-only tooltip. */}
                {showVerificationBadges &&
                  shouldShowVerificationBadge(record, scimEnabled) && (
                    <EmailVerificationBadge
                      interactive={String(record.id) === String(currentUserId)}
                    />
                  )}
              </div>
            </div>
          </div>
        );
      },
    },
    ...(showDetailedColumns
      ? ([
          {
            title: t("team.column.lastActivity"),
            key: "lastActivity",
            dataIndex: "lastActivity",
            render: (value: string | null | undefined) =>
              value ? dayjs(value).fromNow() : "-",
          },
          {
            title: t("team.column.joined"),
            key: "joined",
            render: (_value: unknown, record: TeamMember) => {
              const joined = record.accountData?.joinedAt;
              return joined ? dayjs(joined).format("D MMM YYYY") : "-";
            },
          },
        ] as ColumnsType<TeamMember>)
      : []),
    {
      title: t("team.column.role"),
      key: "role",
      render: (_value: unknown, record: TeamMember) => {
        return (
          <MemberRoleSelection
            member={record}
            viewerRole={currentUserAccountRole}
            disabled={!canManageManually}
            pending={updatingMemberId === String(record.id)}
            onChange={(role) => onUpdateMemberRole(record, role)}
          />
        );
      },
    },
    // AHA-43204: hide the License column entirely on free teams. There's
    // no team-plan to render and no licence to grant/revoke, so the column
    // would only ever render the "unlicensed" placeholder — pure noise.
    // Bulk-select is already gated on isPaidTeamPlan (see TeamPage.tsx
    // `bulkSelectionEligible`), so this keeps the two licence-related
    // affordances consistent.
    ...(isPaidTeamPlan
      ? ([
          {
            title: t("team.column.license"),
            key: "license",
            render: (_value: unknown, record: TeamMember) => {
              // The OWNER can grant/revoke any member's seat — including the
              // owner row (the reseller flow) and their own. AHA-43057: a team
              // ADMIN may only act on plain members (editor/viewer), never the
              // owner or another admin — so gate on isRoleActionableByViewer
              // too (UI-only restriction; the backend has no target-role gate).
              // Still requires
              // non-SCIM (or SCIM + manual-management override); the
              // isPaidTeamPlan gate sits on the column itself now.
              const canEdit =
                currentUserIsTeamOwnerOrAdmin &&
                canManageManually &&
                isRoleActionableByViewer(record, currentUserAccountRole);
              return (
                <MemberLicenseSelection
                  member={record}
                  teamPlan={teamPlan}
                  canEdit={canEdit}
                  canGrantMore={canGrantMore}
                  pending={updatingLicenseMemberId === String(record.id)}
                  onChange={(licensed) => onUpdateMemberLicense(record, licensed)}
                />
              );
            },
          },
        ] as ColumnsType<TeamMember>)
      : []),
    ...(showActionColumn
      ? ([
          {
            title: "",
            key: "actions",
            width: 80,
            render: (_value: unknown, record: TeamMember) => {
              if (canRemove(record)) {
                return (
                  <Tooltip title={t("team.action.removeFromTeam")}>
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => onRemove(record)}
                      className={styles.actionIcon}
                      data-testid="my-team-member-remove-button"
                    />
                  </Tooltip>
                );
              }
              if (canLeave(record)) {
                return (
                  <Tooltip title={t("team.action.leaveTeam")}>
                    <Button
                      type="text"
                      icon={<LogoutOutlined />}
                      onClick={() => onLeave(record)}
                      className={styles.actionIcon}
                      data-testid="my-team-member-leave-button"
                    />
                  </Tooltip>
                );
              }
              return null;
            },
          },
        ] as ColumnsType<TeamMember>)
      : []),
  ];

  // AHA-41755 bulk-action selection. Only mount when the parent opted in
  // (both `selectedMemberIds` and `onSelectionChange` are provided). Personal-
  // plan rows render a disabled checkbox with a tooltip — the licence model
  // doesn't apply to them so they can't be in either cohort.
  const selectionEnabled =
    selectedMemberIds !== undefined && onSelectionChange !== undefined;
  const rowSelection: TableProps<TeamMember>["rowSelection"] = selectionEnabled
    ? {
        selectedRowKeys: selectedMemberIds,
        onChange: (keys) => onSelectionChange(keys.map((k) => String(k))),
        getCheckboxProps: (record) => {
          // Personal-plan rows are opaque to the licence model; AHA-43057:
          // an admin viewer also can't bulk-select the owner or another admin
          // (only plain members), so those rows are non-selectable too.
          const disabled =
            hasPersonalPlan(record, teamPlan) ||
            !isRoleActionableByViewer(record, currentUserAccountRole);
          return {
            disabled,
            name: record.email,
          };
        },
        renderCell: (_checked, record, _index, originNode) => {
          if (hasPersonalPlan(record, teamPlan)) {
            return (
              <Tooltip title={t("team.license.personalPlanHint")}>
                {/* span needed so the tooltip catches hover on the
                    disabled antd checkbox (disabled inputs swallow
                    pointer events). */}
                <span style={{ display: "inline-block" }}>{originNode}</span>
              </Tooltip>
            );
          }
          return originNode;
        },
      }
    : undefined;

  return (
    <div className={styles.wrapper}>
      {header && <div className={styles.header}>{header}</div>}
      <Table<TeamMember>
        rowKey={(m) => String(m.id || m.email)}
        columns={columns}
        dataSource={members}
        loading={loading}
        pagination={false}
        rowSelection={rowSelection}
        // Horizontal scroll on narrow screens (mobile) — without this the
        // extra columns overflow and get clipped by the wrapper's
        // `overflow: hidden`. The min-width tracks the column count so the
        // table still fills the card on desktop and only scrolls when it
        // actually overflows (e.g. mobile). See AHA-41755.
        scroll={{ x: showDetailedColumns ? 720 : 520 }}
      />
    </div>
  );
}
